import React, { useId, useState } from 'react';

export interface AccordionItem {
  id: string;
  title: string;
  /** One-line teaser shown under the title while collapsed. */
  teaser?: string;
  content: React.ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  /** Allow several panels open at once (default: one at a time). */
  multiple?: boolean;
  defaultOpenId?: string;
}

/** Semantic accordion: real buttons, aria-expanded/aria-controls, keyboard native. */
const Accordion: React.FC<AccordionProps> = ({ items, multiple = false, defaultOpenId }) => {
  const base = useId();
  const [open, setOpen] = useState<string[]>(defaultOpenId ? [defaultOpenId] : []);

  const toggle = (id: string) =>
    setOpen((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : multiple ? [...cur, id] : [id],
    );

  return (
    <div className="rounded-2xl border border-secondary-700/60 bg-secondary-900 divide-y divide-secondary-800/70 overflow-hidden">
      {items.map((it) => {
        const isOpen = open.includes(it.id);
        const panelId = `${base}-${it.id}-panel`;
        const btnId = `${base}-${it.id}-btn`;
        return (
          <div key={it.id} className="transition-colors hover:bg-primary-500/5">
            <h3>
              <button
                id={btnId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(it.id)}
                className="w-full min-h-[52px] px-4 sm:px-5 py-3 flex items-center justify-between gap-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400"
              >
                <span className="min-w-0">
                  <span className="block font-semibold text-white">{it.title}</span>
                  {it.teaser && !isOpen && (
                    <span className="block text-sm text-secondary-400 leading-snug mt-0.5">{it.teaser}</span>
                  )}
                </span>
                <svg
                  className={`w-4 h-4 shrink-0 text-primary-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={btnId}
              hidden={!isOpen}
              className="px-4 sm:px-5 pt-3 pb-4 sm:pt-4 text-sm text-secondary-300 leading-relaxed"
            >
              {it.content}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
